<template>
  <Header />
  <div class="tl-page">
    <h1 class="tl-page-title mb-3">Shorex Allotments Germany</h1>

    <!-- ── Filter Bar ── -->
    <div class="tl-filters">
      <div class="tl-filters__selects">
        <!-- Year, Date, Ship, etc selects removed for brevity in this comment but kept in real code -->
        <!-- ... existing selects ... -->
        <select v-model="selectedYear" class="tl-select tl-select--xs" @change="debouncedFetch">
          <option value="" disabled>Year</option>
          <option v-for="year in allYears" :key="year" :value="year">{{ year }}</option>
        </select>

        <div class="tl-select-wrap">
          <select v-model="selectedDate" class="tl-select" @change="debouncedFetch">
            <option value="" disabled>Shorex Date</option>
            <option v-for="date in allDates" :key="date" :value="date">{{ date }}</option>
          </select>
          <span v-if="selectedDate" class="tl-select-clear" @click="clearSelectedDate">✕</span>
        </div>

        <div class="tl-select-wrap">
          <select v-model="selectedUpdateDate" class="tl-select" @change="debouncedFetch">
            <option value="" disabled>Update Date</option>
            <option v-for="updateDate in allUpdateDates" :key="updateDate" :value="updateDate">{{ updateDate }}</option>
          </select>
          <span v-if="selectedUpdateDate" class="tl-select-clear" @click="clearSelectedUpdateDate">✕</span>
        </div>

        <div class="tl-select-wrap">
          <select v-model="selectedCruiseline" class="tl-select" @change="debouncedFetch">
            <option value="" disabled>Cruise Line</option>
            <option v-for="cruiseline in allCruiselines" :key="cruiseline" :value="cruiseline">{{ cruiseline }}</option>
          </select>
          <span v-if="selectedCruiseline" class="tl-select-clear" @click="clearSelectedCruiseline">✕</span>
        </div>

        <div class="tl-select-wrap">
          <select v-model="selectedTourship" class="tl-select" @change="debouncedFetch">
            <option value="" disabled>Ship</option>
            <option v-for="tourship in allTourships" :key="tourship" :value="tourship">{{ tourship }}</option>
          </select>
          <span v-if="selectedTourship" class="tl-select-clear" @click="clearSelectedTourship">✕</span>
        </div>

        <div class="tl-select-wrap">
          <select v-model="selectedTourname" class="tl-select tl-select--wide" @change="debouncedFetch">
            <option value="" disabled>Tour Name</option>
            <option v-for="tourname in allTournames" :key="tourname" :value="tourname">{{ tourname }}</option>
          </select>
          <span v-if="selectedTourname" class="tl-select-clear" @click="clearSelectedTourname">✕</span>
        </div>

        <div class="tl-select-wrap">
          <select v-model="selectedTourcode" class="tl-select tl-select--sm" @change="debouncedFetch">
            <option value="" disabled>Code</option>
            <option v-for="tourcode in allTourcodes" :key="tourcode" :value="tourcode">{{ tourcode }}</option>
          </select>
          <span v-if="selectedTourcode" class="tl-select-clear" @click="clearSelectedTourcode">✕</span>
        </div>

        <div class="tl-select-wrap">
          <select v-model="selectedMxpcode" class="tl-select tl-select--sm" @change="debouncedFetch">
            <option value="" disabled>MXP</option>
            <option v-for="mxpcode in allMxpcodes" :key="mxpcode" :value="mxpcode">{{ mxpcode }}</option>
          </select>
          <span v-if="selectedMxpcode" class="tl-select-clear" @click="clearSelectedMxpcode">✕</span>
        </div>
      </div>

      <div class="tl-filters__actions">
        <div class="tl-search">
          <svg class="tl-search__icon" viewBox="0 0 20 20" fill="none">
            <circle cx="8.5" cy="8.5" r="5.5" stroke="#9ca3af" stroke-width="1.6" />
            <path d="M13 13l3 3" stroke="#9ca3af" stroke-width="1.6" stroke-linecap="round" />
          </svg>
          <input v-model="searchQuery" class="tl-search__input" placeholder="Search tours…" @input="debouncedSearch" />
        </div>
        <span class="tl-count">{{ pagination.totalTours }} Results</span>

        <div class="tl-filter-tools">
          <button class="tl-icon-btn tl-icon-btn--success" @click="plotChart()" v-b-tooltip.hover
            title="Plot Statistics">📊</button>
          <button class="tl-icon-btn tl-icon-btn--warning" @click="resetFilters()" v-b-tooltip.hover
            title="Reset Filters">↺</button>
          <button class="tl-icon-btn tl-icon-btn--primary" @click="showImportModal = true" v-b-tooltip.hover
            title="Import Data">📥</button>
          <button class="tl-icon-btn tl-icon-btn--danger" @click="deleteAllData()" v-b-tooltip.hover
            title="Delete All Data">🗑️</button>
        </div>
      </div>
    </div>

    <!-- ── Loading ── -->
    <div v-if="loading" class="tl-loading">
      <div class="tl-spinner"></div>
      <span>Updating allotment data…</span>
    </div>

    <!-- ── Table ── -->
    <div v-else class="tl-table-card">
      <div class="tl-table-scroll">
        <b-table class="tl-b-table" striped hover :items="tours"
          :fields="[...fields, { key: 'actions', label: 'Actions' }]" v-model:sort-by="sortBy"
          v-model:sort-desc="sortDesc" thead-class="tl-b-table__head" tbody-tr-class="tl-b-table__row" responsive>

          <template #cell(attributes.status)="data">
            <span :class="['tl-pill', data.value === 'Confirmed' ? 'tl-pill--green' : 'tl-pill--blue']">{{ data.value
            }}</span>
          </template>

          <template #cell(actions)="row">
            <div class="tl-action-btns">
              <button class="tl-icon-btn" @click="$router.push({ name: 'Editocean', params: { id: row.item.id } })"
                title="Edit">✏️</button>
              <button class="tl-icon-btn tl-icon-btn--danger" @click="deleteData(row.item.id)"
                title="Delete">🗑</button>
            </div>
          </template>
        </b-table>
      </div>
    </div>

    <!-- ── Pagination ── -->
    <div class="tl-pagination-row mt-4">
      <div class="tl-pagination-info">
        Showing <strong>{{ (pagination.currentPage - 1) * pagination.perPage + 1 }}</strong> to
        <strong>{{ Math.min(pagination.currentPage * pagination.perPage, pagination.totalTours) }}</strong> of
        <strong>{{ pagination.totalTours }}</strong> entries
      </div>
      <div class="tl-pagination-btns">
        <button class="tl-paging-btn" :disabled="pagination.currentPage === 1"
          @click="fetchTours(pagination.currentPage - 1)">
          Previous
        </button>
        <button v-for="page in visiblePages" :key="page" class="tl-paging-btn"
          :class="{ 'tl-paging-btn--active': page === pagination.currentPage }" @click="fetchTours(page)">
          {{ page }}
        </button>
        <button class="tl-paging-btn" :disabled="pagination.currentPage === pagination.totalPages"
          @click="fetchTours(pagination.currentPage + 1)">
          Next
        </button>
      </div>
    </div>

    <!-- ── Import Modal ── -->
    <b-modal v-model="showImportModal" title="Import Data" centered header-class="tl-modal-header"
      footer-class="tl-modal-footer">
      <div class="tl-import-box">
        <label class="tl-file-label">
          <input type="file" ref="fileInput" @change="handleFileChange($event)" accept=".xlsx,.xls,.csv"
            class="tl-file-input" />
          <span class="tl-file-custom">📁 Choose file or drag & drop</span>
        </label>
      </div>
      <template #modal-footer>
        <button class="tl-btn tl-btn--ghost" @click="showImportModal = false">Close</button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import Header from './Header.vue'
import { generateChart } from '../Helper/Chart.js'
import { onFileChange } from '../Helper/Upload.js'
import debounce from 'lodash/debounce'

export default {
  name: 'Ocean',
  components: {
    Header,
  },
  data() {
    const currentYear = new Date().getFullYear()
    return {
      sortBy: 'attributes.date',
      sortDesc: false,
      selectedDate: '',
      selectedUpdateDate: '',
      selectedCruiseline: '',
      selectedTourship: '',
      selectedTourname: '',
      selectedTourcode: '',
      selectedMxpcode: '',
      selectedYear: currentYear,
      searchQuery: '',
      loading: false,
      tours: [],
      allData: [],
      showImportModal: false,
      pagination: {
        currentPage: 1,
        perPage: 12,
        totalTours: 0,
        totalPages: 0
      },
      fields: [
        { key: 'attributes.date', label: 'Shorex Date', sortable: true },
        {
          key: 'attributes.todaydate',
          label: 'Update Date',
          sortable: true,
          formatter: (value) => {
            const date = new Date(value)
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
          }
        },
        { key: 'attributes.tourship', label: 'Ship', sortable: true },
        { key: 'attributes.cruiseline', label: 'Cruise Line', sortable: true },
        { key: 'attributes.tourcode', label: 'Tour Code', sortable: true },
        { key: 'attributes.mxpcode', label: 'MXP Code', sortable: true },
        { key: 'attributes.tourname', label: 'Tour Name', sortable: true },
        { key: 'attributes.maxcapacity', label: 'Max Capacity', sortable: true },
        { key: 'attributes.totalsold', label: 'Total Sold', sortable: true },
        { key: 'attributes.fromtime', label: 'From Time', sortable: true },
        { key: 'attributes.totime', label: 'To Time', sortable: true },
        { key: 'attributes.status', label: 'Status', sortable: true },
      ]
    }
  },
  computed: {
    filteredData() {
      let data = this.allData;
      if (this.selectedYear) {
        data = data.filter(item =>
          new Date(item.attributes.todaydate).getFullYear() === parseInt(this.selectedYear)
        );
      }
      if (this.selectedDate) {
        data = data.filter(item => item.attributes.date === this.selectedDate);
      }
      if (this.selectedUpdateDate) {
        data = data.filter(item => item.attributes.todaydate === this.selectedUpdateDate);
      }
      if (this.selectedCruiseline) {
        data = data.filter(item => item.attributes.cruiseline === this.selectedCruiseline);
      }
      if (this.selectedTourship) {
        data = data.filter(item => item.attributes.tourship === this.selectedTourship);
      }
      if (this.selectedTourname) {
        data = data.filter(item => item.attributes.tourname === this.selectedTourname);
      }
      if (this.selectedTourcode) {
        data = data.filter(item => item.attributes.tourcode === this.selectedTourcode);
      }
      if (this.selectedMxpcode) {
        data = data.filter(item => item.attributes.mxpcode === this.selectedMxpcode);
      }
      return data;
    },
    allYears() {
      let data = this.allData;
      if (this.selectedDate) {
        data = data.filter(item => item.attributes.date === this.selectedDate);
      }
      if (this.selectedUpdateDate) {
        data = data.filter(item => item.attributes.todaydate === this.selectedUpdateDate);
      }
      if (this.selectedCruiseline) {
        data = data.filter(item => item.attributes.cruiseline === this.selectedCruiseline);
      }
      if (this.selectedTourship) {
        data = data.filter(item => item.attributes.tourship === this.selectedTourship);
      }
      if (this.selectedTourname) {
        data = data.filter(item => item.attributes.tourname === this.selectedTourname);
      }
      if (this.selectedTourcode) {
        data = data.filter(item => item.attributes.tourcode === this.selectedTourcode);
      }
      if (this.selectedMxpcode) {
        data = data.filter(item => item.attributes.mxpcode === this.selectedMxpcode);
      }
      return data.length ? [...new Set(data.map(item => new Date(item.attributes.todaydate).getFullYear()))].sort() : [];
    },
    allDates() {
      let data = this.allData;
      if (this.selectedYear) {
        data = data.filter(item =>
          new Date(item.attributes.todaydate).getFullYear() === parseInt(this.selectedYear)
        );
      }
      if (this.selectedUpdateDate) {
        data = data.filter(item => item.attributes.todaydate === this.selectedUpdateDate);
      }
      if (this.selectedCruiseline) {
        data = data.filter(item => item.attributes.cruiseline === this.selectedCruiseline);
      }
      if (this.selectedTourship) {
        data = data.filter(item => item.attributes.tourship === this.selectedTourship);
      }
      if (this.selectedTourname) {
        data = data.filter(item => item.attributes.tourname === this.selectedTourname);
      }
      if (this.selectedTourcode) {
        data = data.filter(item => item.attributes.tourcode === this.selectedTourcode);
      }
      if (this.selectedMxpcode) {
        data = data.filter(item => item.attributes.mxpcode === this.selectedMxpcode);
      }
      return data.length ? [...new Set(data.map(item => item.attributes.date))].sort() : [];
    },
    allUpdateDates() {
      let data = this.allData;
      if (this.selectedYear) {
        data = data.filter(item =>
          new Date(item.attributes.todaydate).getFullYear() === parseInt(this.selectedYear)
        );
      }
      if (this.selectedDate) {
        data = data.filter(item => item.attributes.date === this.selectedDate);
      }
      if (this.selectedCruiseline) {
        data = data.filter(item => item.attributes.cruiseline === this.selectedCruiseline);
      }
      if (this.selectedTourship) {
        data = data.filter(item => item.attributes.tourship === this.selectedTourship);
      }
      if (this.selectedTourname) {
        data = data.filter(item => item.attributes.tourname === this.selectedTourname);
      }
      if (this.selectedTourcode) {
        data = data.filter(item => item.attributes.tourcode === this.selectedTourcode);
      }
      if (this.selectedMxpcode) {
        data = data.filter(item => item.attributes.mxpcode === this.selectedMxpcode);
      }
      return data.length ? [...new Set(data.map(item => item.attributes.todaydate))].sort() : [];
    },
    allCruiselines() {
      let data = this.allData;
      if (this.selectedYear) {
        data = data.filter(item =>
          new Date(item.attributes.todaydate).getFullYear() === parseInt(this.selectedYear)
        );
      }
      if (this.selectedDate) {
        data = data.filter(item => item.attributes.date === this.selectedDate);
      }
      if (this.selectedUpdateDate) {
        data = data.filter(item => item.attributes.todaydate === this.selectedUpdateDate);
      }
      if (this.selectedTourship) {
        data = data.filter(item => item.attributes.tourship === this.selectedTourship);
      }
      if (this.selectedTourname) {
        data = data.filter(item => item.attributes.tourname === this.selectedTourname);
      }
      if (this.selectedTourcode) {
        data = data.filter(item => item.attributes.tourcode === this.selectedTourcode);
      }
      if (this.selectedMxpcode) {
        data = data.filter(item => item.attributes.mxpcode === this.selectedMxpcode);
      }
      return data.length ? [...new Set(data.map(item => item.attributes.cruiseline))].sort() : [];
    },
    allTourships() {
      let data = this.allData;
      if (this.selectedYear) {
        data = data.filter(item =>
          new Date(item.attributes.todaydate).getFullYear() === parseInt(this.selectedYear)
        );
      }
      if (this.selectedDate) {
        data = data.filter(item => item.attributes.date === this.selectedDate);
      }
      if (this.selectedUpdateDate) {
        data = data.filter(item => item.attributes.todaydate === this.selectedUpdateDate);
      }
      if (this.selectedCruiseline) {
        data = data.filter(item => item.attributes.cruiseline === this.selectedCruiseline);
      }
      if (this.selectedTourname) {
        data = data.filter(item => item.attributes.tourname === this.selectedTourname);
      }
      if (this.selectedTourcode) {
        data = data.filter(item => item.attributes.tourcode === this.selectedTourcode);
      }
      if (this.selectedMxpcode) {
        data = data.filter(item => item.attributes.mxpcode === this.selectedMxpcode);
      }
      return data.length ? [...new Set(data.map(item => item.attributes.tourship))].sort() : [];
    },
    allTournames() {
      let data = this.allData;
      if (this.selectedYear) {
        data = data.filter(item =>
          new Date(item.attributes.todaydate).getFullYear() === parseInt(this.selectedYear)
        );
      }
      if (this.selectedDate) {
        data = data.filter(item => item.attributes.date === this.selectedDate);
      }
      if (this.selectedUpdateDate) {
        data = data.filter(item => item.attributes.todaydate === this.selectedUpdateDate);
      }
      if (this.selectedCruiseline) {
        data = data.filter(item => item.attributes.cruiseline === this.selectedCruiseline);
      }
      if (this.selectedTourship) {
        data = data.filter(item => item.attributes.tourship === this.selectedTourship);
      }
      if (this.selectedTourcode) {
        data = data.filter(item => item.attributes.tourcode === this.selectedTourcode);
      }
      if (this.selectedMxpcode) {
        data = data.filter(item => item.attributes.mxpcode === this.selectedMxpcode);
      }
      return data.length ? [...new Set(data.map(item => item.attributes.tourname))].sort() : [];
    },
    allTourcodes() {
      let data = this.allData;
      if (this.selectedYear) {
        data = data.filter(item =>
          new Date(item.attributes.todaydate).getFullYear() === parseInt(this.selectedYear)
        );
      }
      if (this.selectedDate) {
        data = data.filter(item => item.attributes.date === this.selectedDate);
      }
      if (this.selectedUpdateDate) {
        data = data.filter(item => item.attributes.todaydate === this.selectedUpdateDate);
      }
      if (this.selectedCruiseline) {
        data = data.filter(item => item.attributes.cruiseline === this.selectedCruiseline);
      }
      if (this.selectedTourship) {
        data = data.filter(item => item.attributes.tourship === this.selectedTourship);
      }
      if (this.selectedTourname) {
        data = data.filter(item => item.attributes.tourname === this.selectedTourname);
      }
      if (this.selectedMxpcode) {
        data = data.filter(item => item.attributes.mxpcode === this.selectedMxpcode);
      }
      return data.length ? [...new Set(data.map(item => item.attributes.tourcode))].sort() : [];
    },
    allMxpcodes() {
      let data = this.allData;
      if (this.selectedYear) {
        data = data.filter(item =>
          new Date(item.attributes.todaydate).getFullYear() === parseInt(this.selectedYear)
        );
      }
      if (this.selectedDate) {
        data = data.filter(item => item.attributes.date === this.selectedDate);
      }
      if (this.selectedUpdateDate) {
        data = data.filter(item => item.attributes.todaydate === this.selectedUpdateDate);
      }
      if (this.selectedCruiseline) {
        data = data.filter(item => item.attributes.cruiseline === this.selectedCruiseline);
      }
      if (this.selectedTourship) {
        data = data.filter(item => item.attributes.tourship === this.selectedTourship);
      }
      if (this.selectedTourname) {
        data = data.filter(item => item.attributes.tourname === this.selectedTourname);
      }
      if (this.selectedTourcode) {
        data = data.filter(item => item.attributes.tourcode === this.selectedTourcode);
      }
      return data.length ? [...new Set(data.map(item => item.attributes.mxpcode))].sort() : [];
    },
    visiblePages() {
      const range = 2
      const pages = []
      const start = Math.max(1, this.pagination.currentPage - range)
      const end = Math.min(this.pagination.totalPages, this.pagination.currentPage + range)

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    }
  },
  methods: {
    async fetchTours(page = this.pagination.currentPage) {
      this.loading = true;
      try {
        const start = (page - 1) * this.pagination.perPage;
        const queryParams = new URLSearchParams();

        const fields = [
          'date', 'todaydate', 'tourship', 'cruiseline', 'tourcode',
          'mxpcode', 'tourname', 'maxcapacity', 'totalsold', 'fromtime',
          'totime', 'status'
        ];

        fields.forEach((field, index) => {
          queryParams.append(`fields[${index}]`, field);
        });

        queryParams.append('pagination[start]', start);
        queryParams.append('pagination[limit]', this.pagination.perPage);

        if (this.selectedDate) queryParams.append('filters[date][$eq]', this.selectedDate);
        if (this.selectedUpdateDate) queryParams.append('filters[todaydate][$eq]', this.selectedUpdateDate);
        if (this.selectedCruiseline) queryParams.append('filters[cruiseline][$eq]', this.selectedCruiseline);
        if (this.selectedTourship) queryParams.append('filters[tourship][$eq]', this.selectedTourship);
        if (this.selectedTourname) queryParams.append('filters[tourname][$eq]', this.selectedTourname);
        if (this.selectedTourcode) queryParams.append('filters[tourcode][$eq]', this.selectedTourcode);
        if (this.selectedMxpcode) queryParams.append('filters[mxpcode][$eq]', this.selectedMxpcode);
        if (this.selectedYear) {
          queryParams.append('filters[todaydate][$gte]', `${this.selectedYear}-01-01`);
          queryParams.append('filters[todaydate][$lte]', `${this.selectedYear}-12-31`);
        }

        if (this.searchQuery.trim()) {
          const searchTerms = this.searchQuery.trim().split(';').map(term => term.trim()).filter(term => term);
          const searchableFields = ['tourname', 'tourcode', 'cruiseline', 'tourship', 'mxpcode'];

          if (searchTerms.length > 0) {
            searchTerms.forEach((term, termIndex) => {
              searchableFields.forEach((field, fieldIndex) => {
                queryParams.append(`filters[$or][${termIndex * searchableFields.length + fieldIndex}][${field}][$containsi]`, term);
              });
            });
          }
        }

        const sortField = this.sortBy.replace('attributes.', '');
        queryParams.append('sort', `${sortField}:${this.sortDesc ? 'desc' : 'asc'}`);

        const url = `/api/oceans?${queryParams.toString()}`;
        const response = await this.$xhr.get(url);

        this.tours = response.data.data.map(tour => ({
          id: tour.id,
          attributes: tour.attributes
        }));

        this.pagination = {
          currentPage: page,
          perPage: this.pagination.perPage,
          totalTours: response.data.meta.pagination.total,
          totalPages: Math.ceil(response.data.meta.pagination.total / this.pagination.perPage)
        };
      } catch (error) {
        console.error('Error fetching tours:', error);
        this.$bvToast.toast('Failed to load tours. Please try again.', { variant: 'danger', solid: true });
        this.tours = [];
        this.pagination.totalTours = 0;
        this.pagination.totalPages = 0;
      } finally {
        this.loading = false;
      }
    },
    async fetchAllDataForFilters() {
      this.loading = true
      try {
        const queryParams = new URLSearchParams()
        const fields = [
          'date', 'todaydate', 'tourship', 'cruiseline', 'tourcode',
          'mxpcode', 'tourname', 'maxcapacity', 'totalsold', 'fromtime',
          'totime', 'status'
        ]

        fields.forEach((field, index) => {
          queryParams.append(`fields[${index}]`, field)
        })

        const url = `/api/oceans?${queryParams.toString()}`
        const response = await this.$xhr.get(url)

        this.allData = response.data.data.map(tour => ({
          id: tour.id,
          attributes: tour.attributes
        }))
      } catch (error) {
        console.error('Error fetching all data:', error)
        this.$bvToast.toast('Failed to load filter data. Please try again.', { variant: 'danger', solid: true })
        this.allData = []
      } finally {
        this.loading = false
      }
    },
    clearSelectedDate() {
      this.selectedDate = '';
      this.pagination.currentPage = 1;
      this.fetchTours()
    },
    clearSelectedUpdateDate() {
      this.selectedUpdateDate = '';
      this.pagination.currentPage = 1;
      this.fetchTours()
    },
    clearSelectedCruiseline() {
      this.selectedCruiseline = '';
      this.pagination.currentPage = 1;
      this.fetchTours()
    },
    clearSelectedTourship() {
      this.selectedTourship = '';
      this.pagination.currentPage = 1;
      this.fetchTours()
    },
    clearSelectedTourname() {
      this.selectedTourname = '';
      this.pagination.currentPage = 1;
      this.fetchTours()
    },
    clearSelectedTourcode() {
      this.selectedTourcode = '';
      this.pagination.currentPage = 1;
      this.fetchTours()
    },
    clearSelectedMxpcode() {
      this.selectedMxpcode = '';
      this.pagination.currentPage = 1;
      this.fetchTours()
    },
    resetFilters() {
      this.selectedDate = ''
      this.selectedUpdateDate = ''
      this.selectedCruiseline = ''
      this.selectedTourname = ''
      this.selectedTourship = ''
      this.selectedTourcode = ''
      this.selectedMxpcode = ''
      this.selectedYear = new Date().getFullYear()
      this.searchQuery = ''
      this.pagination.currentPage = 1
      this.fetchTours()
    },
    async deleteData(id) {
      if (confirm('Are you sure you want to delete this entry?')) {
        await this.$xhr.delete(`/api/oceans/${id}`)
        await Promise.all([this.fetchTours(), this.fetchAllDataForFilters()])
      }
    },
    async deleteAllData() {
      if (confirm('Are you sure you want to delete all entries?')) {
        const deletePromises = this.tours.map(item =>
          this.$xhr.delete(`/api/oceans/${item.id}`)
        )
        await Promise.all(deletePromises)
        await Promise.all([this.fetchTours(), this.fetchAllDataForFilters()])
      }
    },
    plotChart() {
      generateChart(this)
    },
    handleFileChange(event) {
      onFileChange(event, this)
    },
    debouncedSearch: debounce(function () {
      this.pagination.currentPage = 1
      this.fetchTours()
    }, 300),
    debouncedFetch: debounce(function () {
      this.pagination.currentPage = 1
      this.fetchTours()
    }, 300)
  },
  watch: {
    searchQuery() {
      this.debouncedSearch()
    },
    selectedYear() {
      this.debouncedFetch()
    },
    selectedDate() {
      this.debouncedFetch()
    },
    selectedUpdateDate() {
      this.debouncedFetch()
    },
    selectedCruiseline() {
      this.debouncedFetch()
    },
    selectedTourship() {
      this.debouncedFetch()
    },
    selectedTourname() {
      this.debouncedFetch()
    },
    selectedTourcode() {
      this.debouncedFetch()
    },
    selectedMxpcode() {
      this.debouncedFetch()
    },
    sortBy() {
      this.debouncedFetch()
    },
    sortDesc() {
      this.debouncedFetch()
    }
  },
  async mounted() {
    await Promise.all([this.fetchTours(), this.fetchAllDataForFilters()])
  }
}
</script>
<style scoped>
/* ──────────── Page Layout ──────────── */
.tl-page {
  padding: 80px 24px 20px;
  font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
  max-width: 100vw;
  overflow-x: hidden;
}

.tl-page-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.02em;
}

/* ──────────── Filter Bar ──────────── */
.tl-filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 12px 18px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .05);
}

.tl-filters__selects {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.tl-filters__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.tl-filter-tools {
  display: flex;
  gap: 6px;
  border-left: 1px solid #e5e7eb;
  padding-left: 12px;
}

.tl-select-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.tl-select {
  height: 34px;
  min-width: 130px;
  max-width: 160px;
  font-size: .82rem;
  padding: 0 24px 0 10px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: #f9fafb;
  color: #374151;
  cursor: pointer;
  outline: none;
  transition: all .15s;
}

.tl-select:focus {
  border-color: #E2923D;
  box-shadow: 0 0 0 3px rgba(226, 146, 61, .15);
  background: #fff;
}

.tl-select--wide {
  min-width: 170px;
  max-width: 220px;
}

.tl-select--sm {
  min-width: 100px;
  max-width: 120px;
}

.tl-select--xs {
  min-width: 85px;
  max-width: 100px;
}

.tl-select-clear {
  position: absolute;
  right: 8px;
  cursor: pointer;
  font-size: 0.75rem;
  color: #9ca3af;
  padding: 4px;
}

.tl-select-clear:hover {
  color: #ef4444;
}

/* Search */
.tl-search {
  position: relative;
}

.tl-search__icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
}

.tl-search__input {
  height: 34px;
  padding: 0 12px 0 34px;
  min-width: 220px;
  font-size: .84rem;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: #f9fafb;
  outline: none;
  transition: all .15s;
}

.tl-search__input:focus {
  border-color: #E2923D;
  box-shadow: 0 0 0 3px rgba(226, 146, 61, .15);
  background: #fff;
}

.tl-count {
  background: #fff3e0;
  color: #b45309;
  font-size: .78rem;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 20px;
  white-space: nowrap;
  border: 1px solid #fde68a;
}

/* Buttons */
.tl-btn {
  height: 34px;
  padding: 0 16px;
  font-size: .85rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all .15s;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tl-btn--primary {
  background: #E2923D;
  color: #fff;
}

.tl-btn--success {
  background: #10b981;
  color: #fff;
}

.tl-btn--warning {
  background: #f59e0b;
  color: #fff;
}

.tl-btn--danger {
  background: #ef4444;
  color: #fff;
}

.tl-btn--ghost {
  background: #f3f4f6;
  color: #4b5563;
  border: 1px solid #d1d5db;
}

.tl-btn:hover {
  opacity: .9;
  transform: translateY(-1px);
}

.tl-btn:active {
  transform: translateY(0);
}

/* ──────────── Table Card ──────────── */
.tl-table-card {
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, .04);
}

.tl-table-scroll {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 65vh;
}

.tl-b-table {
  font-size: .83rem;
  margin-bottom: 0;
}

:deep(.tl-b-table__head th) {
  background: #E2923D !important;
  color: #fff !important;
  font-size: .74rem !important;
  font-weight: 750 !important;
  text-transform: uppercase !important;
  letter-spacing: .06em !important;
  border: none !important;
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 12px 14px;
  text-align: center;
}

:deep(.tl-b-table__row:hover td) {
  background: #fff8f1 !important;
}

:deep(.tl-b-table td) {
  padding: 10px 14px;
  vertical-align: middle;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
  text-align: center;
}

/* ──────────── Component Elements ──────────── */
.tl-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: .72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.tl-pill--blue {
  background: #e0e7ff;
  color: #3730a3;
}

.tl-pill--green {
  background: #dcfce7;
  color: #166534;
}

.tl-icon-btn {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all .12s;
  font-size: 0.9rem;
}

.tl-icon-btn:hover {
  background: #f9fafb;
  border-color: #E2923D;
  transform: scale(1.05);
}

.tl-icon-btn--success:hover {
  background: #ecfdf5;
  border-color: #10b981;
}

.tl-icon-btn--warning:hover {
  background: #fffbeb;
  border-color: #f59e0b;
}

.tl-icon-btn--primary:hover {
  background: #fff8f1;
  border-color: #E2923D;
}

.tl-icon-btn--danger:hover {
  background: #fee2e2;
  border-color: #ef4444;
}

.tl-action-btns {
  display: flex;
  gap: 6px;
  justify-content: center;
}

/* ──────────── Pagination ──────────── */
.tl-pagination-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tl-pagination-info {
  font-size: 0.85rem;
  color: #6b7280;
}

.tl-pagination-info strong {
  color: #111827;
}

.tl-pagination-btns {
  display: flex;
  gap: 4px;
}

.tl-paging-btn {
  height: 32px;
  padding: 0 12px;
  font-size: 0.8rem;
  font-weight: 600;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  color: #374151;
  transition: all .15s;
}

.tl-paging-btn:hover:not(:disabled) {
  border-color: #E2923D;
  color: #E2923D;
}

.tl-paging-btn--active {
  background: #E2923D;
  border-color: #E2923D;
  color: #fff;
}

.tl-paging-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f9fafb;
}

/* ──────────── Modal & Other ──────────── */
.tl-import-box {
  padding: 40px 20px;
  display: flex;
  justify-content: center;
}

.tl-file-label {
  cursor: pointer;
}

.tl-file-input {
  display: none;
}

.tl-file-custom {
  padding: 12px 24px;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  color: #6b7280;
  font-weight: 600;
  transition: all .2s;
}

.tl-file-label:hover .tl-file-custom {
  border-color: #E2923D;
  color: #E2923D;
  background: #fffbeb;
}

.tl-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px 0;
  color: #9ca3af;
  font-size: .9rem;
}

.tl-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #f3f4f6;
  border-top-color: #E2923D;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>